import {useCallback} from 'react';

import {EVENT_NAMES, SCREEN_NAMES} from '/enum/tealium.enum';
import {setIsViewTeam, setSelectedTab} from '/redux/action/myTeams.action';
import {getTealiumUtilDefaultInstance} from '/util/tealium';

import {useClearMyTeamsFilters} from './useClearMyTeamsFilters';
import {useMyTeamsContext} from './useMyTeamsContext';

type TabType = 'my-teams' | 'watchlist';

type Props = {
  navigation: any;
  setActiveTab: (tab: TabType) => void;
  coachIDparams?: string;
  defaultFrontlineOnly: boolean;
  defaultNewCoachesKey: string;

  resetSortParams: () => void;
  handleResetFilters: (
    tab?: TabType,
    frontlineOnly?: boolean,
    resetRange?: boolean,
  ) => void;

  resetMyTeamsData?: () => void;
  resetViewTeamsData?: () => void;

  effectiveLastPaidRank?: string;
  effectiveHighestAchievedRank?: string;
  effectiveCoachCurrentRank?: string;
  effectiveUserId?: string;
};

export const useMyTeamsTabs = ({
  setActiveTab,
  coachIDparams,
  defaultFrontlineOnly,
  defaultNewCoachesKey,
  resetSortParams,
  handleResetFilters,
  resetMyTeamsData,
  resetViewTeamsData,
  effectiveLastPaidRank,
  effectiveHighestAchievedRank,
  effectiveCoachCurrentRank,
  effectiveUserId,
}: Props) => {
  const {dispatch} = useMyTeamsContext();
  const clearMyTeamsFilters = useClearMyTeamsFilters();

  const handleTabPress = useCallback(
    (tab: TabType) => {
      const nextIsViewTeam = tab === 'my-teams' && !!coachIDparams;

      if (nextIsViewTeam) {
        dispatch(setIsViewTeam(true));
      }
      dispatch(setSelectedTab(tab));
      setActiveTab(tab);

      // Reset sorting & filters
      resetSortParams();
      clearMyTeamsFilters(defaultNewCoachesKey);

      if (tab === 'my-teams') {
        handleResetFilters('my-teams', defaultFrontlineOnly, false);
      } else {
        handleResetFilters('watchlist');
      }

      // Reset data
      if (resetMyTeamsData && !coachIDparams) {
        resetMyTeamsData();
      }

      if (resetViewTeamsData) {
        resetViewTeamsData();
      }

      // Analytics (use computed value, NOT redux)
      const sendEvent = getTealiumUtilDefaultInstance().generateTrackEvent({
        eventName: EVENT_NAMES.navigationClick,
      });

      sendEvent({
        buttonName: tab === 'my-teams' ? 'my_teams' : 'watchlist',
        menuType: 'tabs',
        screenName: nextIsViewTeam
          ? SCREEN_NAMES.coachTeamsReport
          : tab === 'my-teams'
          ? SCREEN_NAMES.watchListTeamsReport
          : SCREEN_NAMES.myTeamsReport,
        lastPaidRank: effectiveLastPaidRank,
        coach_highest_rank: effectiveHighestAchievedRank,
        coach_current_rank: effectiveCoachCurrentRank,
        coach_id: String(effectiveUserId),
      });
    },
    [
      coachIDparams,
      setActiveTab,
      dispatch,
      resetSortParams,
      clearMyTeamsFilters,
      defaultNewCoachesKey,
      resetMyTeamsData,
      resetViewTeamsData,
      effectiveLastPaidRank,
      effectiveHighestAchievedRank,
      effectiveCoachCurrentRank,
      effectiveUserId,
      handleResetFilters,
      defaultFrontlineOnly,
    ],
  );

  return {handleTabPress};
};
